import React, { useMemo } from 'react';
import { Box, Typography } from '@material-ui/core';
import QRCode from 'react-qr-code';
import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { EditorState, convertFromRaw } from 'draft-js';
import { QRCodeDownloadMenu } from '../../../../../common/qr-code/QRCodeDownloadMenu';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { useOrgInfo } from '../../../../../global/auth/use-org-info/use-org-info';

interface FunnelQRCodePrintPageProps {
  slug: string;
  funnelName: string;
  description?: string | null;
  image?: string | null;
  orgLogo?: string | null;
  trigger: (onClick: (e: React.MouseEvent<HTMLElement>) => void) => React.ReactElement;
  onDone?: () => void;
}

function parseDescription(descriptionJson: string | null): EditorState | null {
  if (!descriptionJson) return null;
  try {
    return EditorState.createWithContent(convertFromRaw(JSON.parse(descriptionJson)));
  } catch {
    return null;
  }
}

export const FunnelQRCodePrintPage: React.FC<FunnelQRCodePrintPageProps> = ({
  slug,
  funnelName,
  description,
  image,
  orgLogo,
  trigger,
  onDone,
}) => {
  const { theme } = useCampfireTheme();
  const orgInfo = useOrgInfo();
  const funnelUrl = `${window.location.origin}/apply/${slug}`;
  const descriptionState = useMemo(() => parseDescription(description ?? null), [description]);

  return (
    <QRCodeDownloadMenu
      value={funnelUrl}
      fileName={`${funnelName} QR code`}
      trigger={trigger}
      onDone={onDone}
      printContent={
        <Box
          style={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            padding: '48px',
            boxSizing: 'border-box',
          }}
        >
          {/* Org logo + name top left */}
          <Box
            style={{
              position: 'absolute',
              top: '40px',
              left: '48px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            {orgLogo && (
              <img src={orgLogo} alt={orgInfo?.name || 'Organisation logo'} style={{ height: '50px', width: 'auto' }} />
            )}
            {orgLogo && orgInfo?.name && <Box style={{ width: '1px', height: '32px', backgroundColor: '#ccc' }} />}
            {orgInfo?.name && (
              <Typography
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: theme.color.grey.neutral400,
                }}
              >
                {orgInfo.name}
              </Typography>
            )}
          </Box>

          {/* Banner image below org header */}
          {image && (
            <Box
              style={{
                position: 'absolute',
                top: '100px',
                left: 0,
                right: 0,
              }}
            >
              <img
                src={image}
                alt={funnelName}
                style={{
                  width: '100%',
                  height: '160px',
                  objectFit: 'cover',
                }}
              />
            </Box>
          )}

          {/* Centered content */}
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '24px',
              width: '100%',
              maxWidth: '600px',
              marginTop: image ? '120px' : 0,
            }}
          >
            <QRCode value={funnelUrl} size={220} />

            <Typography
              style={{
                fontSize: '14px',
                color: theme.color.grey.neutral300,
                textAlign: 'center',
              }}
            >
              Scan to apply
            </Typography>

            <Typography
              style={{
                fontSize: '24px',
                fontWeight: 700,
                color: theme.color.grey.neutral400,
                textAlign: 'center',
              }}
            >
              {funnelName}
            </Typography>

            {descriptionState && (
              <Box
                style={{
                  fontSize: '15px',
                  color: theme.color.grey.neutral300,
                  textAlign: 'center',
                  lineHeight: 1.6,
                  maxHeight: '100px',
                  overflow: 'hidden',
                  width: '100%',
                }}
              >
                <CampfireRichEditor editorState={descriptionState} readOnly />
              </Box>
            )}
          </Box>
        </Box>
      }
    />
  );
};
